import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action, Store } from '@ngrx/store'
import * as Sentry from '@sentry/angular'
import { merge, Observable, of } from 'rxjs'
import { catchError, distinctUntilChanged, filter, finalize, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators'
import { CallerService } from 'src/app/services/caller.service'
import { PushNotificationService } from 'src/app/services/push-notification.service'
import { ChatSendError, SocketIoChatNamespaceService } from 'src/app/services/socket-io.chat-ns.service'
import { chatIdentityOf } from 'src/app/utils/corp-member.util'
import { chatSendErrorCopy, clearStoredOutbox, loadOutbox, saveOutbox } from 'src/app/utils/chat-outbox.util'
import { AppState, ChatSendErrorCode, OutboxEntry } from '../app.state'
import {
  chatMessageFailed,
  chatMessageSent,
  chatMessageWaiting,
  chatOutboxCleared,
  chatOutboxRestored,
  queueChatMessage,
  removeOutboxMessage,
  retryChatMessage,
} from '../actions/chat-outbox.actions'
import { newChatMessage, setCorpMemberProfileData, updateMessages } from '../actions/corp-member.actions'
import { selectChatOutbox, selectFeatureCorpMember } from '../selectors/corp.selectors'

/**
 * Owns sending chat messages, app-wide - so a send finishes (and a message that wasn't sent is
 * kept) wherever the sender is by then, not only while the Messages page is open.
 *
 * The chat outbox (the `chat_outbox` state) holds the sender's messages the server hasn't saved.
 * A message goes straight out if the socket is up, else waits; waiting messages - and ones that
 * failed for a reason worth retrying - are resent by themselves whenever the socket (re)connects.
 * The outbox is kept on this device for the logged-in corp member, so a reload doesn't lose them.
 */
@Injectable()
export class ChatOutboxEffects {
  // client_msg_ids with a send running - a tap on Retry during a reconnect's resend doesn't
  // start a second one.
  private inFlight = new Set<string>()
  // Counted only once a send is confirmed - for the push-notification prompt after 3 sends.
  private sentThisSession = 0
  // Whose saved outbox has been loaded into the store: saving before then would overwrite it
  // with the (still empty) store.
  private restoredFor: string | undefined

  send$ = createEffect(() =>
    this.actions$.pipe(
      ofType(queueChatMessage, retryChatMessage),
      withLatestFrom(this.store.select(selectChatOutbox)),
      map(([action, outbox]) => {
        const clientMsgId = action.type === queueChatMessage.type ? action.entry.outgoing.client_msg_id : action.client_msg_id
        return { entry: outbox?.[clientMsgId], auto: action.type === retryChatMessage.type && !!action.auto }
      }),
      filter((sending): sending is { entry: OutboxEntry; auto: boolean } =>
        !!sending.entry && !this.inFlight.has(sending.entry.outgoing.client_msg_id)),
      mergeMap(({ entry, auto }) => this.send(entry, auto)),
    ),
  )

  resendOnConnect$ = createEffect(() =>
    merge(
      this.chatSocket.connected$.pipe(distinctUntilChanged(), filter(Boolean)),
      this.actions$.pipe(ofType(chatOutboxRestored)),
    ).pipe(
      filter(() => this.chatSocket.isConnected),
      withLatestFrom(this.store.select(selectChatOutbox)),
      mergeMap(([, outbox]) =>
        Object.values(outbox ?? {})
          .filter((entry) => entry.status === 'waiting' || (entry.status === 'failed' && entry.autoRetry))
          .sort((a, b) => a.message.created_at.localeCompare(b.message.created_at))
          .map((entry) => retryChatMessage({ client_msg_id: entry.outgoing.client_msg_id, auto: true })),
      ),
    ),
  )

  // setCorpMemberProfileData is dispatched on every login and app boot.
  restore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setCorpMemberProfileData),
      map(({ data }) => {
        const owner = chatIdentityOf(data)
        const entries = loadOutbox(owner)
        this.restoredFor = owner
        return entries
      }),
      filter((entries) => entries.length > 0),
      map((entries) => chatOutboxRestored({ entries })),
    ),
  )

  persist$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          queueChatMessage, retryChatMessage, chatMessageWaiting, chatMessageSent, chatMessageFailed,
          removeOutboxMessage, chatOutboxRestored, newChatMessage, updateMessages,
        ),
        withLatestFrom(this.store.select(selectChatOutbox), this.store.select(selectFeatureCorpMember)),
        tap(([, outbox, corper]) => {
          const owner = chatIdentityOf(corper)
          if (owner && owner === this.restoredFor) saveOutbox(owner, outbox ?? {})
        }),
      ),
    { dispatch: false },
  )

  clear$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatOutboxCleared),
        tap(() => {
          clearStoredOutbox()
          this.restoredFor = undefined
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private chatSocket: SocketIoChatNamespaceService,
    private callerService: CallerService,
    private pushNotificationService: PushNotificationService,
  ) {}

  /** `auto`: resent by itself on reconnect - its failure isn't announced again. */
  private send(entry: OutboxEntry, auto: boolean): Observable<Action> {
    const clientMsgId = entry.outgoing.client_msg_id
    if (!this.chatSocket.isConnected) {
      return of(chatMessageWaiting({ client_msg_id: clientMsgId }))
    }

    this.inFlight.add(clientMsgId)
    return this.chatSocket.sendChatMessage(entry.outgoing).pipe(
      map((ack) => {
        this.afterSent(entry)
        return chatMessageSent({ client_msg_id: clientMsgId, chat: ack?.chat })
      }),
      catchError((error: unknown) => {
        const code: ChatSendErrorCode = error instanceof ChatSendError ? error.code : 'SERVER_ERROR'
        // The socket went down while it was being sent: it goes when the socket's back.
        if (code === 'TIMEOUT' && !this.chatSocket.isConnected) {
          return of(chatMessageWaiting({ client_msg_id: clientMsgId }))
        }
        if (!auto) this.announceFailure(code)
        return of(chatMessageFailed({ client_msg_id: clientMsgId, error: code }))
      }),
      finalize(() => this.inFlight.delete(clientMsgId)),
    )
  }

  private afterSent(entry: OutboxEntry): void {
    this.sentThisSession++
    if (entry.draft.mentions.length > 0 || this.sentThisSession >= 3) {
      this.pushNotificationService.maybePromptToEnable()
    }
  }

  private announceFailure(code: ChatSendErrorCode): void {
    this.callerService.showNotification(chatSendErrorCopy(code))
    // How often sends fail, and why - the number that says whether sending is healthy. The
    // backend's own reasons are logged there; these are the ones the sender actually saw.
    if (code === 'TIMEOUT' || code === 'SERVER_ERROR') {
      Sentry.captureMessage('Chat message not sent', { level: 'warning', tags: { chat_send_error: code } })
    }
  }
}
